// /admin/logs — read-only audit log of every activity_events row across all
// users. Newest-first, paginated by simple limit. Rows are grouped by
// author so admins can scan per-person activity at a glance.

import { getSDK } from '../../functions/sdk.js'

const _LIMIT = 200

const _typeLabel = {
  standup: 'Standup',
  ticket_update: 'Ticket update',
  note_edit: 'Note edit',
  kb_edit: 'KB edit',
  meet_call: 'Meet call'
}

const _typeColor = (t) =>
  t === 'standup' ? 'green'
    : t === 'meet_call' ? 'purple'
      : t === 'kb_edit' || t === 'note_edit' ? 'cerulean'
        : 'blue'

const _shortTime = (ts) => {
  try {
    return new Date(ts).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })
  } catch { return '' }
}

const _shortDate = (ts) => {
  try {
    const d = new Date(ts)
    const today = new Date()
    const sameDay = d.toDateString() === today.toDateString()
    if (sameDay) return 'Today'
    const yest = new Date(); yest.setDate(today.getDate() - 1)
    if (d.toDateString() === yest.toDateString()) return 'Yesterday'
    const sameYear = d.getFullYear() === today.getFullYear()
    return d.toLocaleDateString(undefined, sameYear ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' })
  } catch { return '' }
}

const _refFor = (s) => {
  const md = s.metadata || {}
  if (md.ticket_number) return `#${md.ticket_number}`
  if (s.source_id) return String(s.source_id).slice(0, 8)
  return ''
}

const _titleFor = (s) => {
  const md = s.metadata || {}
  return md.title || md.summary || md.body || s.source_table || ''
}

const _hrefFor = (s) => {
  const md = s.metadata || {}
  switch (s.activity_type) {
    case 'ticket_update':
      return md.ticket_number ? `/tickets/my?ticket=${md.ticket_number}` : '/tickets/my'
    case 'standup':
      return '/tickets/standups'
    case 'note_edit':
      return s.source_id ? `/documents/notes?id=${s.source_id}` : '/documents/notes'
    case 'kb_edit':
      return s.source_id ? `/documents/knowledge-base?id=${s.source_id}` : '/documents/knowledge-base'
    case 'meet_call':
      return s.source_id ? `/meet?room=${s.source_id}` : '/meet'
    default:
      return null
  }
}

const _openRef = (el, s) => {
  // ticket_update opens the ticket modal in-place (same UX as the ticket cards
  // elsewhere in the app); other types route to the owning page.
  if (s.activity_type === 'ticket_update') {
    const md = s.metadata || {}
    const tickets = s.root?.myTickets || []
    const ticket = tickets.find(t => (t.id === s.source_id) || (md.ticket_number && (t.number === md.ticket_number || t.id === md.ticket_number)))
    if (ticket) {
      s.root?.update({ selectedTicket: { ...ticket } })
      return
    }
  }
  const href = _hrefFor(s)
  if (!href) return
  let root = el; while (root && !root.routes) root = root.parent
  if (root) el.router(href, root)
}

const _dayKey = (r) => r.occurred_on || (r.created_at ? r.created_at.slice(0, 10) : '')

const _groupByDayThenAuthor = (rows) => {
  const days = new Map()
  for (const r of rows) {
    const day = _dayKey(r)
    if (!days.has(day)) days.set(day, new Map())
    const authors = days.get(day)
    const email = r.user_email || '—'
    if (!authors.has(email)) authors.set(email, [])
    authors.get(email).push(r)
  }
  return [...days.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([day, authors]) => ({
      _day: day,
      _authors: [...authors.entries()]
        .map(([email, events]) => ({
          _email: email,
          _events: events,
          _count: events.length,
          _totalScore: events.reduce((a, e) => a + (e.score || 0), 0),
          _latestAt: events.reduce((a, e) => Math.max(a, new Date(e.created_at).getTime() || 0), 0)
        }))
        .sort((a, b) => b._latestAt - a._latestAt),
      _totalEvents: [...authors.values()].reduce((a, evs) => a + evs.length, 0),
      _totalScore: [...authors.values()].reduce((a, evs) => a + evs.reduce((s, e) => s + (e.score || 0), 0), 0)
    }))
}

export const adminLogs = {
  extends: 'AppPage',
  metadata: { title: 'Logs / Admin / Symbols Workspace' },
  onInit: (el, s) => {
    if (!el.call('shouldRedirectNoPerm', s, 'admin.pages.read')) return
    const root = el.call('findRouteRoot', el)
    if (root) el.router('/', root, {}, { pushState: true })
  },

  onCreate: async (el, s) => {
    const root = s.root
    if (!root) return
    let email = root.currentUserEmail
    if (!email) {
      await new Promise(resolve => {
        let attempts = 0
        const check = setInterval(() => {
          email = root.currentUserEmail
          if (email || ++attempts > 30) { clearInterval(check); resolve() }
        }, 200)
      })
    }
    if (!email) return
    try {
      // migrated 2026-04-27 to SDK route workspaceProject.auditLog/list
      const result = await getSDK().execute('workspaceProject.auditLog', 'list', {
        filter: {},
        options: {
          columns: 'id, user_email, activity_type, score, source_table, source_id, metadata, created_at, occurred_on',
          order: { created_at: 'desc' },
          limit: _LIMIT
        }
      })
      const data = Array.isArray(result) ? result : (result?.data || result?.rows || result?.events || [])
      root.update({ adminLogs: data || [] })
    } catch (err) {
      console.error('[admin-logs]', err)
      root.update({ adminLogs: [] })
    }
  },

  AdminSidebar: {
    Bottom: {
      flow: 'y',
      gap: 'Z1',
      padding: 'A',
      borderTopWidth: '1px',
      borderTopStyle: 'solid',
      borderTopColor: 'line',

      SectionTitle: {
        tag: 'span',
        fontSize: 'Y1',
        fontWeight: '700',
        letterSpacing: '.04em',
        textTransform: 'uppercase',
        color: 'caption',
        text: 'Filter by date'
      },

      Calendar: { extends: 'AdminLogCalendar' },

      ClearBtn: {
        hide: (el, s) => !s.root?._adminLogsSelectedDate,
        tag: 'button',
        type: 'button',
        padding: 'V Z1',
        round: 'Z',
        border: 'none',
        background: 'overlay',
        color: 'subtitle',
        fontSize: 'Y2',
        fontWeight: '600',
        cursor: 'pointer',
        alignSelf: 'flex-start',
        text: '× Clear date filter',
        ':hover': { background: 'overlayStrong', color: 'title' },
        onClick: (e, el, s) => s.root?.update({ _adminLogsSelectedDate: null })
      }
    }
  },

  // Scale-only admin power surface (cross-workspace audit trail).
  // Collab features stay free for any org; this page is paid.
  UpgradeGate: {
    extends: 'UpgradeBanner',
    requires: 'scale',
    hide: (el, s) => el.call('isScale', s.root)
  },

  AdminMain: {
    extends: 'AppMain',
    padding: '0',
    hide: (el, s) => !el.call('isScale', s.root),

    Header: {
      extends: 'PageHeader',
      LeftGroup: { Title: { text: 'Activity Logs' } }
    },

    Body: {
      overflow: 'auto',
      padding: 'B',
      flow: 'y',
      gap: 'A',

      Intro: {
        tag: 'p',
        margin: '0',
        fontSize: 'Z2',
        color: 'caption',
        lineHeight: '1.5',
        text: (el, s) => {
          const rows = (s.root?.adminLogs || []).filter(r => !s.root?._adminLogsSelectedDate || _dayKey(r) === s.root._adminLogsSelectedDate)
          const authors = new Set(rows.map(r => r.user_email)).size
          const scope = s.root?._adminLogsSelectedDate ? `on ${_shortDate(s.root._adminLogsSelectedDate)}` : 'across all dates'
          return `${rows.length} events ${scope} from ${authors} ${authors === 1 ? 'person' : 'people'}.`
        }
      },

      Empty: {
        hide: (el, s) => {
          const rows = (s.root?.adminLogs || []).filter(r => !s.root?._adminLogsSelectedDate || _dayKey(r) === s.root._adminLogsSelectedDate)
          return rows.length > 0
        },
        padding: 'A',
        color: 'caption',
        fontStyle: 'italic',
        text: 'No activity events for this filter.'
      },

      Days: {
        flow: 'y',
        gap: 'B',
        childExtends: 'AdminLogDay',
        childrenAs: 'state',
        children: (el, s) => {
          const rows = (s.root?.adminLogs || [])
            .filter(r => !s.root?._adminLogsSelectedDate || _dayKey(r) === s.root._adminLogsSelectedDate)
          return _groupByDayThenAuthor(rows)
        }
      }
    }
  }
}

export const AdminLogDay = {
  flow: 'y',
  gap: 'Z1',

  DayHeader: {
    flow: 'x',
    align: 'baseline space-between',
    gap: 'Z',
    paddingBottom: 'V',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'line',

    Title: {
      tag: 'h3',
      margin: '0',
      fontSize: 'A',
      fontWeight: '700',
      color: 'title',
      text: (el, s) => _shortDate(s._day)
    },
    Stats: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      text: (el, s) => `${s._totalEvents} event${s._totalEvents === 1 ? '' : 's'}`
    }
  },

  Authors: {
    flow: 'y',
    gap: 'Z1',
    childExtends: 'AdminLogGroup',
    childrenAs: 'state',
    children: (el, s) => s._authors
  }
}

export const AdminLogGroup = {
  flow: 'y',
  round: 'Z2',
  background: 'overlay',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',
  overflow: 'hidden',

  Header: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z1',
    padding: 'Z A',
    background: 'overlayStrong',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'line',

    LeftGroup: {
      flow: 'x',
      align: 'center left',
      gap: 'Z',

      Avatar: {
        width: 'A',
        height: 'A',
        round: '50%',
        background: (el, s) => {
          const me = el.call('findPerson', s._email)
          return me?.avatar ? `url(${me.avatar}) center/cover` : `var(--color-blue)`
        },
        flexShrink: '0'
      },
      Name: {
        tag: 'span',
        fontSize: 'Z2',
        fontWeight: '700',
        color: 'title',
        text: (el, s) => {
          const me = el.call('findPerson', s._email)
          return me?.name || s._email
        }
      },
      Email: {
        hide: (el, s) => {
          const me = el.call('findPerson', s._email)
          return !me?.name
        },
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        text: (el, s) => s._email
      }
    },

    Stats: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      text: (el, s) => `${s._count} event${s._count === 1 ? '' : 's'} · ${s._totalScore} pts`
    }
  },

  Events: {
    flow: 'y',
    childExtends: 'AdminLogRow',
    childrenAs: 'state',
    children: (el, s) => s._events
  }
}

// ── Calendar (mini month view) ─────────────────────────────────────────
const _monthGrid = (year, month) => {
  // Returns 6×7 = 42 cells for the month view (leading/trailing days included).
  const first = new Date(year, month, 1)
  const startDow = (first.getDay() + 6) % 7 // Monday-first
  const start = new Date(year, month, 1 - startDow)
  const cells = []
  for (let i = 0; i < 42; i++) {
    const d = new Date(start)
    d.setDate(start.getDate() + i)
    cells.push({
      _date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
      _day: d.getDate(),
      _inMonth: d.getMonth() === month
    })
  }
  return cells
}

export const AdminLogCalendar = {
  flow: 'y',
  gap: 'Z',
  padding: 'A',
  round: 'Z2',
  background: 'overlay',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',

  onCreate: (el, s) => {
    const root = s.root
    if (!root) return
    const today = new Date()
    if (root._adminLogsCalYear == null || root._adminLogsCalMonth == null) {
      root.update({
        _adminLogsCalYear: today.getFullYear(),
        _adminLogsCalMonth: today.getMonth()
      })
    }
  },

  Nav: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',

    Prev: {
      tag: 'button',
      type: 'button',
      width: 'A1',
      height: 'A1',
      round: 'Z',
      border: 'none',
      cursor: 'pointer',
      background: 'transparent',
      color: 'subtitle',
      fontSize: 'Z2',
      fontWeight: '700',
      text: '‹',
      ':hover': { background: 'overlayStrong', color: 'title' },
      onClick: (e, el, s) => {
        const y = s.root?._adminLogsCalYear ?? new Date().getFullYear()
        const m = s.root?._adminLogsCalMonth ?? new Date().getMonth()
        const next = new Date(y, m - 1, 1)
        s.root?.update({ _adminLogsCalYear: next.getFullYear(), _adminLogsCalMonth: next.getMonth() })
      }
    },

    Label: {
      tag: 'span',
      flex: '1',
      textAlign: 'center',
      fontSize: 'Z2',
      fontWeight: '700',
      color: 'title',
      text: (el, s) => {
        const y = s.root?._adminLogsCalYear ?? new Date().getFullYear()
        const m = s.root?._adminLogsCalMonth ?? new Date().getMonth()
        return new Date(y, m, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
      }
    },

    Next: {
      tag: 'button',
      type: 'button',
      width: 'A1',
      height: 'A1',
      round: 'Z',
      border: 'none',
      cursor: 'pointer',
      background: 'transparent',
      color: 'subtitle',
      fontSize: 'Z2',
      fontWeight: '700',
      text: '›',
      ':hover': { background: 'overlayStrong', color: 'title' },
      onClick: (e, el, s) => {
        const y = s.root?._adminLogsCalYear ?? new Date().getFullYear()
        const m = s.root?._adminLogsCalMonth ?? new Date().getMonth()
        const next = new Date(y, m + 1, 1)
        s.root?.update({ _adminLogsCalYear: next.getFullYear(), _adminLogsCalMonth: next.getMonth() })
      }
    }
  },

  Dows: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 'X',
    fontSize: 'Y1',
    color: 'caption',
    fontWeight: '600',
    textAlign: 'center',

    M: { text: 'M' },
    T1: { text: 'T' },
    W: { text: 'W' },
    T2: { text: 'T' },
    F: { text: 'F' },
    Sa: { text: 'S' },
    Su: { text: 'S' }
  },

  Grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 'X',

    childExtends: 'AdminLogCalendarCell',
    childrenAs: 'state',
    children: (el, s) => {
      const y = s.root?._adminLogsCalYear ?? new Date().getFullYear()
      const m = s.root?._adminLogsCalMonth ?? new Date().getMonth()
      return _monthGrid(y, m)
    }
  }
}

export const AdminLogCalendarCell = {
  tag: 'button',
  type: 'button',
  aspectRatio: '1',
  round: 'Z',
  border: 'none',
  cursor: (el, s) => s._inMonth ? 'pointer' : 'default',
  fontSize: 'Y2',
  fontWeight: '600',
  fontFamily: 'inherit',
  transition: 'background 100ms ease',
  background: (el, s) => {
    if (s.root?._adminLogsSelectedDate === s._date) return 'blue'
    const has = (s.root?.adminLogs || []).some(r => _dayKey(r) === s._date)
    if (has) return 'blue.15'
    return 'transparent'
  },
  color: (el, s) => {
    if (s.root?._adminLogsSelectedDate === s._date) return 'white'
    if (!s._inMonth) return 'caption'
    const has = (s.root?.adminLogs || []).some(r => _dayKey(r) === s._date)
    return has ? 'blue' : 'subtitle'
  },
  opacity: (el, s) => s._inMonth ? '1' : '.4',
  ':hover': {
    background: (el, s) => {
      if (s.root?._adminLogsSelectedDate === s._date) return 'blue'
      return s._inMonth ? 'overlayStrong' : 'transparent'
    }
  },
  text: (el, s) => String(s._day),
  onClick: (e, el, s) => {
    if (!s._inMonth) return
    const cur = s.root?._adminLogsSelectedDate
    s.root?.update({ _adminLogsSelectedDate: cur === s._date ? null : s._date })
  }
}

export const AdminLogRow = {
  flow: 'x',
  gap: 'Z1',
  align: 'baseline left',
  padding: 'Z A',
  fontFamily: 'mono',
  fontSize: 'Y2',
  lineHeight: '1.55',
  borderTopWidth: '1px',
  borderTopStyle: 'solid',
  borderTopColor: 'line',
  ':hover': { background: 'white.04' },

  When: {
    flow: 'y',
    gap: '0',
    flexShrink: '0',
    minWidth: 'D',

    Date: {
      tag: 'span',
      color: 'subtitle',
      fontWeight: '600',
      text: (el, s) => _shortDate(s.occurred_on || s.created_at)
    },
    Time: {
      tag: 'span',
      color: 'caption',
      fontSize: 'Y1',
      text: (el, s) => _shortTime(s.created_at)
    }
  },

  Type: {
    tag: 'span',
    flexShrink: '0',
    minWidth: 'C1',
    fontWeight: '700',
    color: (el, s) => _typeColor(s.activity_type),
    text: (el, s) => _typeLabel[s.activity_type] || s.activity_type || ''
  },

  Score: {
    tag: 'span',
    flexShrink: '0',
    minWidth: 'A2',
    textAlign: 'right',
    color: 'caption',
    fontWeight: '600',
    text: (el, s) => `+${s.score || 0}`
  },

  Ref: {
    hide: (el, s) => !_refFor(s),
    tag: 'button',
    type: 'button',
    flexShrink: '0',
    minWidth: 'B1',
    padding: '0',
    border: 'none',
    background: 'transparent',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: '700',
    cursor: (el, s) => _hrefFor(s) ? 'pointer' : 'default',
    color: (el, s) => _typeColor(s.activity_type),
    textAlign: 'left',
    textDecoration: 'underline',
    textDecorationColor: 'transparent',
    transition: 'text-decoration-color 100ms ease',
    ':hover': { textDecorationColor: 'currentColor' },
    text: (el, s) => _refFor(s),
    onClick: (e, el, s) => {
      e.stopPropagation()
      _openRef(el, s)
    }
  },

  Detail: {
    tag: 'span',
    flex: '1',
    color: 'title',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => _titleFor(s)
  }
}
